import Vue from "vue";
import { createUUIDv4 } from "../util/helpers";
import { eventBus } from '../util/eventbus';
import { ApckAlertDialog } from "./dialogs/alert";
import { ApckConfirmDialog, ApckConfirmCheckDialog, ApckConfirmMultiCheckDialog, ApckConfirmScopeDialog } from "./dialogs/confirm";
import { ApckPromptDialog } from "./dialogs/prompt";
import { ApckSelectDialog } from "./dialogs/select";

export const ApckLightboxModal = {
    props: {
        cancelOnClose: {
          type: Boolean,
          default: false,
        },
        uuid: {
            type: String,
            default: "",
        },
        title: {
            type: String,
            default: "",
        },
        size: {
            type: String,
            default: "normal",
        },
        position: {
            // TODO: add vertically centered
            // "" - default 15% from top
            // top - - 2% from top
            // centered (not implemented)
            type: String,
            default: "",
        },
        shadowPrompt: {
            type: Boolean,
            default: false,
        },
        shadowPromptMessage: {
            type: String,
            default: "Are you sure you wish to close this popup?"
        }
    },
    methods: {
        close() {
            if (this.cancelOnClose) {
                this.$emit("cancel", this.uuid);
            }
            this.$emit("close", this.uuid);
            
        },
        closeShadow() {
            const cmp = this;
            const options = {
                title: "Confirmation",
                component: ApckConfirmDialog,
                bind: {
                    message: cmp.shadowPromptMessage,
                },
                on: {
                    accept: function () {
                        cmp.$nextTick(() => {
                            cmp.$emit("close", cmp.uuid);
                        });
                    },
                }
            };
            eventBus.$emit("lightbox:open", options);
        },
    },
    computed: {
        lightboxStyle() {
            return ` lightbox-${this.size}`;
        },
        lightboxPosition() {
            if (this.position) {
                return ` lightbox-${this.position}`;
            }
            return '';
        },
    },
    template: `
        <div class="lightbox">
            <div v-if="shadowPrompt" class="lightbox-shadow" v-on:click="closeShadow"></div>
            <div v-else class="lightbox-shadow" v-on:click="close"></div>
            <slot v-if="size === 'contents'"></slot>
            <div v-else :class="['lightbox-outer', lightboxPosition]" ref="lightboxouter">
                <div class="lightbox-title">
                    <div class="title-bar">
                        <span class="title-text">{{title}}</span>
                    </div>
                    <span class="lightbox-close" v-on:click="close">
                        <i class="icon fa fa-close"></i>
                    </span>
                </div>
                <div :class="['lightbox-inner', lightboxStyle]">
                    <slot></slot>
                </div>
            </div>
        </div>
    `,
};

export const ApckLightboxHolder = {
    components: {
        ApckLightboxModal,
    },
    created() {},
    data() {
        return {
            container: [],
        };
    },
    methods: {
        addLightbox(options) {
            const cmp = this;

            // TODO: test scroll lock and mobile devices
            if (cmp.container.length === 0) {
                document.body.classList.add('lightbox-scroll-lock');
            }

            const defaults = {
                uuid: createUUIDv4(),
                component: "",
                bind: {},
                on: {},
                // open: true,
                // speed: 200,
                size: null,
                shadowClose: true,
                // cleanup: true,
                title: '',
                shadowPrompt: false,
                shadowPromptMessage: "Are you sure you wish to close this popup?",
                beforeclose: function(){},
                beforeopen: function(){}
            };

            const lightboxOptions = Object.assign(defaults, options);
            // beforeopen
            if (lightboxOptions.beforeopen)
                lightboxOptions.beforeopen()
            cmp.container.push(Object.assign(lightboxOptions));
            // ?nextTick
        },
        close(uuid, data = null) {
            const index = this.container.findIndex((f) => f.uuid === uuid);
            if (index === -1) return;
            // beforeclose
            if (this.container[index].beforeclose)
                this.container[index].beforeclose(data)
            // ?nextTick
            this.container.splice(index, 1);

            // TODO: test scroll lock and mobile devices
            if (this.container.length === 0) {
                document.body.classList.remove('lightbox-scroll-lock');
            }
        },
        clearAll() {
            for (let i = 0; i < this.container.length; i += 1) {
                const lightbox = this.container[i];
                if (typeof (lightbox.beforeclose) === 'function') {
                    lightbox.beforeclose();
                }
            }
            this.container = [];
            document.body.classList.remove('lightbox-scroll-lock');
        },
        closeLatest() {
            const lightbox = this.container[this.container.length - 1];
            if (typeof (lightbox.beforeclose) === 'function') {
                lightbox.beforeclose();
            }

            this.container.splice((this.container.length - 1), 1);

            if (lightbox.on && typeof lightbox.on.close === 'function') {
                lightbox.on.close();
            }

            if (this.container.length === 0) {
                document.body.classList.remove('lightbox-scroll-lock');
            }
        },
    },
    mounted() {
        eventBus.$on("lightbox:open", this.addLightbox);
        eventBus.$on("lightbox:clearAll", this.clearAll);
    },
    template: `
    <div>
    <transition-group name="lightbox-fade">
        <apck-lightbox-modal
            v-for="lightbox in container"
            :uuid="lightbox.uuid"
            :key="lightbox.uuid"
            :title="lightbox.title"
            :size="lightbox.size"
            :position="lightbox.position"
            :errors="lightbox.errors"
            :shadowPrompt="lightbox.shadowPrompt"
            :shadowPromptMessage="lightbox.shadowPromptMessage"
            :cancelOnClose="lightbox.cancelOnClose"
            v-on:close="closeLatest"
            v-on:cancel="lightbox.rejectCallback()"
        >
            <component
                :key="lightbox.uuid"
                :is="lightbox.component"
                v-bind="lightbox.bind"
                v-on="lightbox.on"
                v-on:close="data => close(lightbox.uuid, data)"
            >
            </component>
        </apck-lightbox-modal>
    </transition-group>
    </div>
    `,
};

const defineLightbox = () => {
    const lightbox = {};
    lightbox.open = (options) => {
        eventBus.$emit("lightbox:open", options);
    };
    lightbox.clearAll = () => {
        eventBus.$emit("lightbox:clearAll");
    };
    // lightbox.close = (id) => {
    // }
    return lightbox;
};

const defineDialog = () => {
    const dialog = {};
    dialog.alert = (message, title, opts = {}) => {
        if (!title) {
            title = "Alert";
        }
        const options = {
            title,
            component: ApckAlertDialog,
            bind: { message, html: opts.html || false },
        };
        eventBus.$emit("lightbox:open", options);
    };

    dialog.confirm = (message, title, acceptCallback, rejectCallback, confirmation, closeCallback, cancelOnClose = null) => {
        // TODO: Messages
        if (!title) {
            title = "Confirm";
        }

        // {with_input: true, input: 'DISABLE'},
        var confDefaults = {with_input: false, input:'', with_note:false, above_note_text:'Type notes in here'};
        const confirmationOptions = Object.assign(confDefaults, confirmation);

        const options = {
            title,
            component: ApckConfirmDialog,
            cancelOnClose,
            rejectCallback,
            beforeclose: closeCallback || (() => {}),
            bind: {
                message,
                confirmWithInput: confirmationOptions.with_input,
                confirmWithInputValue: confirmationOptions.input,
                confirmWithNote: confirmationOptions.with_note,
                aboveNotesText: confirmationOptions.above_note_text
            },
            on: {
                accept: function () {
                    if (acceptCallback) acceptCallback();
                },
                deny: function () {
                    if (rejectCallback) rejectCallback();
                },
                note: function (value) {
                    if (acceptCallback) acceptCallback(value);
                },
            }
        };
        eventBus.$emit("lightbox:open", options);
    };

    dialog.confirmScope = (acceptCallback, rejectCallback, confirmation, closeCallback) => {
        // TODO: Messages
        let title = "Scope Addition Notice";

        // {with_input: true, input: 'DISABLE'},
        var confDefaults = {with_input: false, input:'', with_note:false, above_note_text:'Type notes in here'};
        const confirmationOptions = Object.assign(confDefaults, confirmation);

        const options = {
            title,
            size: 'normal',
            component: ApckConfirmScopeDialog,
            beforeclose: closeCallback || (() => {}),
            bind: {
                confirmWithInput: confirmationOptions.with_input,
                confirmWithInputValue: confirmationOptions.input,
                confirmWithNote: confirmationOptions.with_note,
                aboveNotesText: confirmationOptions.above_note_text
            },
            on: {
                accept: function () {
                    if (acceptCallback) acceptCallback();
                },
                deny: function () {
                    if (rejectCallback) rejectCallback();
                },
                note: function (value) {
                    if (acceptCallback) acceptCallback(value);
                },
            }
        };
        eventBus.$emit("lightbox:open", options);
    };

    dialog.confirmCheck = (message, title, checkLabel, acceptCallback, rejectCallback, defaultCheckboxValue = false) => {
        if (!title) {
            title = "Confirm";
        }

        const options = {
            title,
            component: ApckConfirmCheckDialog,
            bind: {
                message,
                label: checkLabel,
                defaultCheckboxValue,
            },
            on: {
                accept: function (value) {
                    if (acceptCallback) acceptCallback(value);
                },
                deny: function () {
                    if (rejectCallback) rejectCallback();
                },
            }
        };
        eventBus.$emit("lightbox:open", options);
    };

    // Multi-checkbox variant. checks = [{key, label, default?}, ...]
    // acceptCallback receives a plain object {key: bool, ...}.
    dialog.confirmMultiCheck = (message, title, checks, acceptCallback, rejectCallback) => {
        if (!title) title = 'Confirm';
        const options = {
            title,
            component: ApckConfirmMultiCheckDialog,
            bind: { message, checks },
            on: {
                accept: function (values) {
                    if (acceptCallback) acceptCallback(values);
                },
                deny: function () {
                    if (rejectCallback) rejectCallback();
                },
            },
        };
        eventBus.$emit("lightbox:open", options);
    };

    dialog.prompt = (message, title, defaultValue, acceptCallback, rejectCallback) => {
        if (!title) title = 'Prompt';
        if (!defaultValue) defaultValue = '';
        const options = {
            title,
            component: ApckPromptDialog,
            bind: { message, label: title, value: defaultValue, rows: 0 },
            beforeclose: (data) => {
                if (!data) return;
                if (data.status && acceptCallback) acceptCallback(data.value);
                if (!data.status && rejectCallback) rejectCallback();
            },
        };
        eventBus.$emit("lightbox:open", options);
    };

    dialog.promptLarge = (message, title, defaultValue, acceptCallback, rejectCallback) => {
        if (!title) title = 'Prompt';
        if (!defaultValue) defaultValue = '';
        const options = {
            title,
            component: ApckPromptDialog,
            bind: { message, label: title, value: defaultValue, rows:10 },
            beforeclose: (data) => {
                if (!data) return;
                if (data.status && acceptCallback) acceptCallback(data.value);
                if (!data.status && rejectCallback) rejectCallback();
            },
        };
        eventBus.$emit("lightbox:open", options);
    };

    dialog.promptPassword = (message, title, defaultValue, acceptCallback, rejectCallback) => {
        if (!title) title = 'Prompt';
        if (!defaultValue) defaultValue = '';
        const options = {
            title,
            component: ApckPromptDialog,
            bind: { message, label: title, value: defaultValue, rows: 0, type: "password" },
            beforeclose: (data) => {
                if (!data) return;
                if (data.status && acceptCallback) acceptCallback(data.value);
                if (!data.status && rejectCallback) rejectCallback();
            },
        };
        eventBus.$emit("lightbox:open", options);
    };

    dialog.select = (message, title, values, acceptCallback, rejectCallback) => {
        if (!title) title = 'Select';
        if (!values) {
            values = [];
        }
        const options = {
            title,
            component: ApckSelectDialog,
            bind: { message, label: title, options: values },
            beforeclose: (data) => {
                if (!data) return;
                if (data.status && acceptCallback) acceptCallback(data.value);
                if (!data.status && rejectCallback) rejectCallback();
            },
        };
        eventBus.$emit("lightbox:open", options);
    };

    return dialog;
};

// plugin
const ApckLightbox = {};

ApckLightbox.install = (Vue) => {
    Vue.component("apck-lightbox-holder", ApckLightboxHolder);
    Vue.prototype.$lightbox = defineLightbox();
    Vue.prototype.$dialog = defineDialog();
};

export default ApckLightbox;
